import React from "react";
import { X, Wallet, Loader2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";

export const formatWallet = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "₹0.00";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN");
  } catch {
    return String(d);
  }
};

const formatTxType = (type) => {
  if (!type) return "—";
  if (type === "CREDIT") return "Cash reward";
  if (type === "SHARE") return "Sent balance";
  if (type === "RECHARGE") return "Recharge";
  if (type === "Admin") return "Admin";
  return String(type);
};

/** Strip ADMIN ADD/DEDUCT prefix for cleaner note display. */
const formatAdminNote = (paymentMethod) => {
  const raw = String(paymentMethod || "").trim();
  if (!raw) return "—";
  if (raw.toUpperCase().startsWith("NAME_CHANGE_FEE")) {
    return "Name change fee";
  }
  const cleaned = raw.replace(/^ADMIN\s+(ADD|DEDUCT)\s*:?\s*/i, "").trim();
  return cleaned || raw;
};

const formatAmount = (amount, type) => {
  const n = parseFloat(String(amount ?? "0"));
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toFixed(2);
  if (type === "Admin") {
    return n >= 0 ? `+₹${abs}` : `-₹${abs}`;
  }
  if (type === "CREDIT" || type === "RECHARGE") return `+₹${abs}`;
  if (type === "SHARE") return `-₹${abs}`;
  return `₹${abs}`;
};

const amountClass = (amount, type) => {
  const n = parseFloat(String(amount ?? "0"));
  const isCredit =
    type === "CREDIT" ||
    type === "RECHARGE" ||
    (type === "Admin" && n >= 0);
  if (isCredit) return "text-green-700 dark:text-green-400 font-medium";
  if (type === "SHARE" || (type === "Admin" && n < 0))
    return "text-red-600 dark:text-red-400 font-medium";
  return "text-gray-900 dark:text-white font-medium";
};

const CustomerWalletSidebar = ({
  open,
  onClose,
  customer,
  walletData,
  isLoading,
  isFetching,
  error,
  onRefresh,
  onUpdateBalance,
}) => {
  if (!open || !customer) return null;

  const displayName =
    [customer.name, customer.lastName].filter(Boolean).join(" ").trim() ||
    customer.name ||
    customer.phone ||
    "Customer";

  const balance = walletData?.balance ?? customer.walletBalance ?? 0;
  const transactions = walletData?.transactions ?? [];

  return (
  <>
    <div
      className="fixed inset-0 z-40 bg-black/40"
      onClick={onClose}
      aria-hidden="true"
    />
    <aside
      className="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col"
      role="dialog"
      aria-label="Customer wallet details"
    >
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <Wallet className="h-5 w-5 shrink-0" />
            <h2 className="text-lg font-semibold truncate">Wallet details</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
            {displayName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 font-mono truncate">
            {customer._id}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="shrink-0 h-8 w-8 p-0">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Available balance
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {formatWallet(balance)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
            <Button size="sm" onClick={onUpdateBalance}>
              Adjust balance
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            All transactions ({transactions.length})
          </h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading transactions…
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600 dark:text-red-400 text-sm">
            Failed to load transactions.
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
            No transactions found for this user.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx._id || tx.transactionId}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {formatDate(tx.timestamp || tx.createdAt)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {formatTxType(tx.transactionType)}
                    </TableCell>
                    <TableCell
                      className={`text-xs whitespace-nowrap ${amountClass(tx.amount, tx.transactionType)}`}
                    >
                      {formatAmount(tx.amount, tx.transactionType)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {tx.paymentStatus || "—"}
                    </TableCell>
                    <TableCell
                      className="text-xs max-w-[160px] truncate"
                      title={tx.paymentMethod || ""}
                    >
                      {formatAdminNote(tx.paymentMethod)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </aside>
  </>
  );
};

export default CustomerWalletSidebar;
